import { Flexbox, Form } from '@/components'
import useAuth from '@/hooks/useAuth'
import yup from '@/lib/yupPt'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { validateCPF } from 'validations-br'
import { cnpj } from '@/lib/validators'
import session from '@/services/auth/session'
import PropTypes from 'prop-types'
import AvatarUpload from './AvatarUpload'
import FormFields from './FormFields'

const schema = yup.object().shape({
  fullName: yup.string().trim().required('O nome é obrigatório'),
  displayName: yup.string().trim().required('O apelido é obrigatório'),
  documentNumber: yup.string().test(
    'is-document-number',
    'Documento inválido',
    (value = '') => (value?.length < 12 ? validateCPF(value) : cnpj(value)),
  ).required(),
  phoneNumber: yup.string().trim().required('Número de telefone é obrigatório'),
  birthdayDate: yup.date().nullable(),
})

function FormProfile({ onUpdated }) {
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [photoPreview, setPhotoPreview] = useState(null)
  const {
    licensing, setLicensing, refreshUser,
  } = useAuth()

  const currentPhoto = photoPreview || licensing?.licensed?.photoURL

  const onSubmit = useCallback(
    async (body) => {
      try {
        setLoading(true)
        const payload = { ...body }
        if (photoPreview) {
          payload.photoURL = photoPreview
        }
        await session.updateCurrentUser(payload)
        setLoading(false)
        toast.success('Dados atualizados com sucesso!')
        setLicensing({ ...licensing, licensed: { ...licensing?.licensed, ...payload } })
        await refreshUser()
        setPhotoPreview(null)
        setActive(false)
        if (onUpdated) await onUpdated()
      } catch {
        setLoading(false)
      }
    },
    [photoPreview, setLicensing, licensing, refreshUser, onUpdated],
  )

  const handleClose = useCallback(() => {
    setActive(false)
    setPhotoPreview(null)
  }, [])

  return (
    <>
      <div className="text-right">
        <button
          onClick={() => setActive(true)}
          className="btn btn-primary"
          type="button"
        >
          <i className="fas fa-edit" />
          &nbsp;Editar dados
        </button>
      </div>
      <div className={`modal ${active ? 'active' : ''}`}>
        <a
          type="button"
          className="modal-overlay"
          aria-label="Close"
          onClick={handleClose}
        />
        <div className="modal-container">
          <fieldset disabled={loading}>
            <Form.Formik
              initialValues={{
                fullName: licensing?.licensed?.fullName || '',
                displayName: licensing?.licensed?.displayName || '',
                documentNumber: licensing?.licensed?.documentNumber || '',
                phoneNumber: licensing?.licensed?.phoneNumber || '',
                birthdayDate: licensing?.licensed?.birthdayDate
                  ? new Date(licensing.licensed.birthdayDate)
                  : null,
              }}
              validationSchema={schema}
              onSubmit={onSubmit}
              validateOnMount
            >
              {({
                errors, touched, isValid, setFieldValue, values,
              }) => (
                <Form.ValidationForm>
                  <div className="modal-header">
                    <a
                      type="button"
                      className="btn btn-clear float-right"
                      onClick={handleClose}
                    />
                    <div className="modal-title h5">Editar dados</div>
                  </div>
                  <div className="modal-body">
                    <Flexbox.Columns>
                      <AvatarUpload
                        name={licensing?.licensed?.fullName}
                        currentPhotoURL={currentPhoto}
                        onPhotoUploaded={setPhotoPreview}
                      />
                      <FormFields
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </Flexbox.Columns>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className={`btn btn-primary ${loading ? 'loading' : ''}`}
                      disabled={loading || !isValid}
                    >
                      <i className="fas fa-check" />
                      &nbsp;Salvar dados
                    </button>
                  </div>
                </Form.ValidationForm>
              )}
            </Form.Formik>
          </fieldset>
        </div>
      </div>
    </>
  )
}

FormProfile.propTypes = {
  onUpdated: PropTypes.func.isRequired,
}

export default FormProfile
